import { useEffect, useRef, useState, type FormEvent } from 'react';
import { themes, type ThemeKey } from './themes';
import { CONTENT_BASE, CONTENT_INK, CONTENT_MUTED, CONTENT_BORDER } from './contentTokens';
import {
  subscribeApplyPrefill,
  consumeApplyPrefill,
  mergeApplyMessage,
} from '@/utils/applyPrefill';
import TuitionCalculatorSection from '@/pages/home/components/TuitionCalculatorSection';
import TrustPanelSection from '@/pages/home/components/TrustPanelSection';
import QualificationSection from '@/pages/home/components/QualificationSection';
import CompareSection from '@/pages/home/components/CompareSection';

type SectionId = 'studio' | 'calc' | 'trust' | 'qualify' | 'compare' | 'services' | 'contact';

type Props = {
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
  theme: ThemeKey;
  scrollTarget: 'top' | SectionId | null;
  onScrollHandled: () => void;
  prefillSubject?: string | null;
  onPrefillConsumed?: () => void;
};

const HANDLE_PEEK = 26;

const FORM_ENDPOINT = 'https://readdy.ai/api/form/daoti8dscq3ovkco3ph0';

const SECTION_LABELS: Record<SectionId, string> = {
  studio: '소개',
  calc: '자부담 계산기',
  trust: '신뢰 지표',
  qualify: '자가진단',
  compare: '과정비교',
  services: '지원분야',
  contact: '문의',
};

const SECTION_ORDER: SectionId[] = ['studio', 'calc', 'trust', 'qualify', 'compare', 'services', 'contact'];

const SERVICES = [
  {
    k: '창업지원',
    d: '사업화 자금과 멘토링, 시제품 제작까지 창업의 전 과정을 함께 지원합니다.',
    m: ['연 320+ 사업', '최대 1억 원'],
    p: '최대 1억 원',
  },
  {
    k: '취업지원',
    d: '1:1 취업 상담부터 일자리 매칭, 면접 대비까지 취업 전 과정을 돕습니다.',
    m: ['연 480+ 사업', '전국 17개 시·도'],
    p: '상담 무료',
  },
  {
    k: '교육·훈련',
    d: '직무 훈련과 디지털 역량 강화 과정을 무료로 안내하고 수료까지 돕습니다.',
    m: ['연 400+ 과정', '수강료 무료'],
    p: '수강료 무료',
  },
] as const;

const PROCESS = [
  { n: '1단계', l: '탐색', d: '내 조건에 맞는 국비지원 사업을 검색하고 한눈에 비교합니다.' },
  { n: '2단계', l: '상담', d: '신청 자격과 준비 서류를 전문 상담사와 함께 확인합니다.' },
  { n: '3단계', l: '신청', d: '온라인으로 접수하고 진행 상황을 알림으로 받아봅니다.' },
] as const;

export default function ContentPage({
  open, onClose, onOpen, theme, scrollTarget, onScrollHandled,
  prefillSubject, onPrefillConsumed,
}: Props) {
  const V = themes[theme];
  const accent = V.bg;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState<SectionId>('studio');

  // form
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [interest, setInterest] = useState<'창업' | '취업' | '교육'>('창업');
  const [studioSlide, setStudioSlide] = useState(0);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const STUDIO_SLIDES = [
    {
      src: 'https://readdy.ai/api/search-image?query=A%20bright%20minimal%20public%20service%20consultation%20center%20with%20a%20counselor%20greeting%20a%20visitor%20across%20a%20light%20wooden%20table%2C%20warm%20natural%20window%20light%2C%20editorial%20documentary%20photography%2C%20muted%20warm%20neutral%20palette%2C%20calm%20welcoming%20mood%2C%20film%20grain&width=1200&height=1400&seq=dabom-center-1&orientation=portrait',
      alt: '다봄 상담센터',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=A%20small%20team%20of%20public%20service%20staff%20reviewing%20documents%20together%20in%20a%20bright%20minimal%20office%20with%20large%20windows%2C%20warm%20natural%20light%2C%20editorial%20documentary%20photography%2C%20muted%20warm%20neutral%20palette%2C%20film%20grain&width=1200&height=1400&seq=dabom-center-2&orientation=portrait',
      alt: '공공데이터 연동팀',
    },
  ];

  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => {
      setStudioSlide((s) => (s + 1) % STUDIO_SLIDES.length);
    }, 5000);
    return () => clearInterval(id);
  }, [open, STUDIO_SLIDES.length]);

  // 갤러리 카드에서 넘어온 사업명을 문의 폼에 자동 첨부
  useEffect(() => {
    if (!prefillSubject) return;
    setSubject(prefillSubject);
    setMessage((m) => (m.trim() ? m : `${prefillSubject} 자격 확인과 신청 상담을 요청드립니다.`));
    onPrefillConsumed?.();
  }, [prefillSubject, onPrefillConsumed]);

  // 계산기 · 자가진단 결과를 문의내용에 자동 첨부
  useEffect(() => {
    const unsubscribe = subscribeApplyPrefill((text) => {
      setMessage((prev) => mergeApplyMessage(prev, text));
    });
    const pending = consumeApplyPrefill();
    if (pending) {
      setMessage((prev) => mergeApplyMessage(prev, pending));
    }
    return unsubscribe;
  }, []);

  const translateY = open ? `-${HANDLE_PEEK}px` : `calc(100vh - ${HANDLE_PEEK}px)`;

  useEffect(() => {
    if (!open || !scrollTarget) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const t = setTimeout(() => {
      if (scrollTarget === 'top') {
        scroller.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const el = scroller.querySelector<HTMLElement>(`#${scrollTarget}`);
        if (el) scroller.scrollTo({ top: Math.max(0, el.offsetTop - 72), behavior: 'smooth' });
      }
      onScrollHandled();
    }, 950);
    return () => clearTimeout(t);
  }, [open, scrollTarget, onScrollHandled]);

  useEffect(() => {
    if (!open) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const onScroll = () => {
      const y = scroller.scrollTop + 140;
      let current: SectionId = 'studio';
      for (const id of SECTION_ORDER) {
        const el = scroller.querySelector<HTMLElement>(`#${id}`);
        if (el && el.offsetTop <= y) current = id;
      }
      setActiveSection(current);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [open]);

  const scrollToSection = (id: SectionId) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const el = scroller.querySelector<HTMLElement>(`#${id}`);
    if (el) scroller.scrollTo({ top: Math.max(0, el.offsetTop - 72), behavior: 'smooth' });
  };

  const goContact = () => scrollToSection('contact');

  const scrollToTop = () => {
    scrollerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onSubmitInquiry = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);
    const honey = (fd.get('company_alt') || '').toString().trim();
    if (honey) {
      setFormStatus('success');
      formEl.reset();
      setInterest('창업');
      setSubject('');
      setMessage('');
      return;
    }
    if (honey === '') fd.delete('company_alt');

    const message = (fd.get('message') || '').toString();
    if (message.length > 500) {
      setFormStatus('error');
      setFormError('문의 내용은 500자 이내로 작성해 주세요.');
      return;
    }

    setFormStatus('sending');
    setFormError('');

    try {
      const body = new URLSearchParams();
      fd.forEach((v, k) => body.append(k, v.toString()));
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || text || '';
      const isSpam = /spam/i.test(serverMsg);
      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus('success');
        formEl.reset();
        setInterest('창업');
        setSubject('');
        setMessage('');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '문제가 발생했어요. 다시 시도해 주세요.');
      }
    } catch {
      setFormStatus('error');
      setFormError('네트워크 오류가 발생했어요. 다시 시도해 주세요.');
    }
  };

  return (
    <div className="fixed inset-0 z-[95]" style={{ pointerEvents: 'none' }} aria-hidden={!open}>
      <div
        className="absolute left-0 right-0 top-0"
        style={{
          height: `calc(100vh + ${HANDLE_PEEK}px)`,
          transform: `translateY(${translateY})`,
          transition: open
            ? 'transform 0.9s cubic-bezier(0.7, 0, 0.15, 1)'
            : 'transform 0.75s cubic-bezier(0.7, 0, 0.15, 1)',
          willChange: 'transform',
        }}
      >
        {/* Pull-tab */}
        <button
          type="button"
          onClick={open ? onClose : onOpen}
          aria-label={open ? '갤러리로 돌아가기' : '다봄 열기'}
          className="lumen-handle-tab absolute top-0 left-1/2 flex items-center justify-center cursor-pointer"
          style={{
            transform: 'translateX(-50%)',
            width: '132px',
            height: `${HANDLE_PEEK + 14}px`,
            background: CONTENT_BASE,
            borderTopLeftRadius: '999px',
            borderTopRightRadius: '999px',
            boxShadow: '0 -6px 24px -6px rgba(30,25,20,0.14)',
            pointerEvents: 'auto',
          }}
        >
          <span
            className="absolute top-[7px] left-1/2 -translate-x-1/2 block rounded-full transition-all duration-500"
            style={{
              width: '28px', height: '2px',
              background: open ? '#c8bfb6' : accent,
              opacity: open ? 0.7 : 0.9,
            }}
          />
          <span
            className="absolute left-1/2 flex items-center justify-center transition-transform duration-500"
            style={{
              top: '14px', width: '14px', height: '14px',
              transform: open ? 'translateX(-50%) rotate(180deg)' : 'translateX(-50%) rotate(0deg)',
            }}
          >
            <i className="ri-arrow-up-s-line" style={{ fontSize: '16px', color: CONTENT_MUTED, lineHeight: 1 }} />
          </span>
        </button>

        {/* Scrollable body */}
        <div
          ref={scrollerRef}
          className="absolute left-0 right-0 overflow-y-auto"
          style={{
            top: `${HANDLE_PEEK}px`,
            height: '100vh',
            background: CONTENT_BASE,
            color: CONTENT_INK,
            pointerEvents: open ? 'auto' : 'none',
          }}
        >
          {/* Sticky header */}
          <div
            className="sticky top-0 z-20 flex items-center justify-between px-8 md:px-14 py-5"
            style={{
              background: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
              borderBottom: `1px solid ${CONTENT_BORDER}`,
            }}
          >
            <button
              type="button"
              onClick={scrollToTop}
              className="flex items-baseline gap-[10px] leading-none cursor-pointer"
            >
              <span className="font-logo-b text-[22px]" style={{ letterSpacing: '-0.03em', lineHeight: 1, color: CONTENT_INK }}>
                다봄
              </span>
              <span className="text-[9px] tracking-[0.42em] uppercase leading-none" style={{ color: CONTENT_MUTED }}>
                국비지원 통합 플랫폼
              </span>
            </button>

            <nav className="hidden md:flex items-center gap-5 lg:gap-7">
              {SECTION_ORDER.map((id) => {
                const isActive = activeSection === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => scrollToSection(id)}
                    className="cursor-pointer text-[10px] tracking-[0.2em] uppercase whitespace-nowrap leading-none transition-colors"
                    style={{ color: isActive ? accent : CONTENT_INK, opacity: isActive ? 1 : 0.55 }}
                  >
                    {SECTION_LABELS[id]}
                  </button>
                );
              })}
            </nav>

            <button
              type="button"
              onClick={onClose}
              className="cursor-pointer flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase whitespace-nowrap transition-colors hover:opacity-70"
              style={{ color: CONTENT_INK }}
            >
              <i className="ri-arrow-down-line text-[15px]" />
              갤러리로 돌아가기
            </button>
          </div>

          {/* ─────────── 01 소개 ─────────── */}
          <section id="studio" className="max-w-[1180px] mx-auto px-8 md:px-14 pt-24 pb-28">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16 items-stretch">
              {/* left */}
              <div className="lg:col-span-6 flex flex-col justify-between">
                <div className="text-[11px] font-semibold tracking-wide mb-6" style={{ color: accent }}>
                  플랫폼 소개
                </div>
                <h2
                  className="font-logo-b text-3xl md:text-4xl leading-tight mb-8"
                  style={{ color: CONTENT_INK, letterSpacing: '-0.04em' }}
                >
                  국비지원,<br />한 곳에서.
                </h2>
                <p className="mb-5" style={{ fontSize: '16px', lineHeight: 1.7, color: CONTENT_INK, opacity: 0.85 }}>
                  다봄은 흩어져 있던 정부·지자체 국비지원 정보를 공공데이터로
                  한데 모은 통합 플랫폼입니다.
                </p>
                <p className="mb-10" style={{ fontSize: '16px', lineHeight: 1.7, color: CONTENT_INK, opacity: 0.72 }}>
                  복잡한 신청 조건과 일정을 한눈에 정리해, 나에게 맞는 지원사업을
                  놓치지 않도록 돕습니다.
                </p>

                {/* soft rounded metadata capsules */}
                <div className="flex flex-wrap gap-2.5">
                  {[
                    ['데이터', '공공데이터 실시간 연계'],
                    ['지원 규모', '연 1,200+ 사업'],
                    ['이용', '무료'],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      className="inline-flex items-center gap-3 pl-4 pr-5 h-[38px] rounded-full transition-colors"
                      style={{ background: 'transparent', border: `1px solid ${CONTENT_BORDER}` }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = `${accent}12`; e.currentTarget.style.borderColor = `${accent}55`; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = CONTENT_BORDER; }}
                    >
                      <span className="text-[9px] tracking-[0.32em] uppercase" style={{ color: CONTENT_MUTED }}>{k}</span>
                      <span className="text-[13px]" style={{ color: CONTENT_INK }}>{v}</span>
                    </div>
                  ))}
                </div>

              </div>

              {/* right — asymmetric drop */}
              <div className="lg:col-span-6">
                <div
                  className="relative w-full overflow-hidden"
                  style={{ height: '440px', borderRadius: '48px', background: '#ecd8c9' }}
                >
                  {STUDIO_SLIDES.map((sl, i) => (
                    <img
                      key={sl.src}
                      src={sl.src}
                      alt={sl.alt}
                      className="absolute inset-0 w-full h-full transition-opacity duration-[900ms] ease-out"
                      style={{ objectFit: 'cover', objectPosition: 'center', opacity: studioSlide === i ? 1 : 0 }}
                    />
                  ))}
                  <div className="absolute bottom-5 right-6 flex items-center gap-2 z-10">
                    {STUDIO_SLIDES.map((_, i) => {
                      const isActive = studioSlide === i;
                      return (
                        <button
                          key={i}
                          type="button"
                          aria-label={`${i + 1}번 사진 보기`}
                          onClick={() => setStudioSlide(i)}
                          className="cursor-pointer rounded-full transition-all"
                          style={{ width: isActive ? 22 : 8, height: 8, background: isActive ? '#fff' : 'rgba(255,255,255,0.55)' }}
                        />
                      );
                    })}
                  </div>
                </div>
                {/* legacy hidden */}
                <div className="hidden" aria-hidden="true">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20bright%20minimal%20public%20service%20consultation%20center%20with%20a%20counselor%20greeting%20a%20visitor%20across%20a%20light%20wooden%20table%2C%20warm%20natural%20window%20light%2C%20editorial%20documentary%20photography%2C%20muted%20warm%20neutral%20palette%2C%20calm%20welcoming%20mood%2C%20film%20grain&width=1200&height=1400&seq=dabom-center-1&orientation=portrait"
                    alt="다봄 상담센터"
                    className="w-full h-full"
                    style={{ objectFit: 'cover', objectPosition: 'center' }}
                  />
                </div>

                {/* team */}
                <div className="grid grid-cols-2 gap-8 mt-8 px-2">
                  {[
                    { n: '다봄 상담센터', r: '전국 17개 시·도' },
                    { n: '공공데이터 연동팀', r: '실시간 공고 수집' },
                  ].map((p, i) => {
                    const isActive = studioSlide === i;
                    return (
                      <button
                        key={p.n}
                        type="button"
                        onClick={() => setStudioSlide(i)}
                        className="text-left cursor-pointer transition-opacity"
                        style={{ opacity: isActive ? 1 : 0.5 }}
                      >
                        <div className="text-[15px] mb-1" style={{ color: CONTENT_INK }}>{p.n}</div>
                        <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: isActive ? accent : CONTENT_MUTED }}>
                          {p.r}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          {/* ─────────── 02~05 자부담 계산기 · 신뢰 지표 · 자가진단 · 과정비교 ─────────── */}
          <TuitionCalculatorSection theme={theme} onGoContact={goContact} />
          <TrustPanelSection theme={theme} />
          <QualificationSection theme={theme} onGoContact={goContact} />
          <CompareSection theme={theme} />

          {/* ─────────── 06 지원분야 ─────────── */}
          <section id="services" className="max-w-[1180px] mx-auto px-8 md:px-14 pt-16 pb-24">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
              <div className="lg:col-span-6">
                <div className="text-[11px] font-semibold tracking-wide mb-6" style={{ color: accent }}>
                  분야별 핵심 지원
                </div>
                <h2
                  className="font-logo-b text-3xl md:text-4xl leading-tight"
                  style={{ color: CONTENT_INK, letterSpacing: '-0.04em' }}
                >
                  지원사업,<br />분야별로.
                </h2>
              </div>
              <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                <p style={{ fontSize: '15px', lineHeight: 1.7, color: CONTENT_INK, opacity: 0.7 }}>
                  창업·취업·교육, 세 분야를 중심으로 지금 신청할 수 있는 국비지원
                  사업을 정리했습니다. 조건과 일정은 공고마다 다르니 상담을 통해
                  확인하세요.
                </p>
              </div>
            </div>

            {/* Large rounded service bands */}
            <div className="space-y-5">
              {SERVICES.map((s, idx) => (
                <div
                  key={s.k}
                  className="group relative overflow-hidden transition-all duration-300"
                  style={{
                    borderRadius: '36px',
                    background: 'transparent',
                    border: `1px solid ${CONTENT_BORDER}`,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${accent}12`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center px-8 md:px-10 py-8 md:py-9">
                    <div className="md:col-span-4 flex items-center gap-5">
                      <span
                        className="inline-flex items-center justify-center rounded-full text-[11px] font-semibold"
                        style={{ padding: '0 12px', height: 28, background: '#fff', border: `1px solid ${CONTENT_BORDER}`, color: accent }}
                      >
                        {`지원 ${idx + 1}`}
                      </span>
                      <div className="font-logo-b text-[34px] md:text-[38px] leading-none" style={{ color: CONTENT_INK, letterSpacing: '-0.04em' }}>
                        {s.k}
                      </div>
                    </div>
                    <div className="md:col-span-5">
                      <p style={{ fontSize: '15px', lineHeight: 1.6, color: CONTENT_INK, opacity: 0.82, marginBottom: 10 }}>
                        {s.d}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {s.m.map((li) => (
                          <span
                            key={li}
                            className="text-[11px] px-3 h-[26px] inline-flex items-center rounded-full"
                            style={{ background: '#fff', color: CONTENT_MUTED, border: `1px solid ${CONTENT_BORDER}` }}
                          >
                            {li}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="md:col-span-3 md:text-right">
                      <div className="text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: CONTENT_MUTED }}>지원 규모</div>
                      <div className="font-logo-b text-[22px]" style={{ color: accent, letterSpacing: '-0.02em' }}>
                        {s.p}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Process — lightweight rhythm */}
            <div className="mt-20">
              <div className="text-[10px] tracking-[0.42em] uppercase mb-8" style={{ color: CONTENT_MUTED }}>
                신청 절차
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {PROCESS.map((p) => (
                  <div key={p.n} className="flex items-start gap-4">
                    <span
                      className="inline-flex items-center justify-center rounded-full text-[11px] font-semibold shrink-0"
                      style={{ width: 44, height: 28, background: `${accent}15`, color: accent }}
                    >
                      {p.n}
                    </span>
                    <div className="pt-1">
                      <div className="text-[15px] mb-1.5" style={{ color: CONTENT_INK }}>{p.l}</div>
                      <div className="text-[13px]" style={{ color: CONTENT_INK, opacity: 0.65, lineHeight: 1.55 }}>
                        {p.d}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </section>

          {/* ─────────── 07 문의 ─────────── */}
          <section id="contact" className="max-w-[1180px] mx-auto px-8 md:px-14 pt-16 pb-28">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-16 items-stretch">
              {/* Left */}
              <div className="lg:col-span-5 flex flex-col justify-between">
                <div className="text-[11px] font-semibold tracking-wide mb-6" style={{ color: accent }}>
                  1:1 빠른 문의
                </div>
                <h2
                  className="font-logo-b text-3xl md:text-4xl leading-tight mb-8"
                  style={{ color: CONTENT_INK, letterSpacing: '-0.04em' }}
                >
                  궁금한 점,<br />다봄에<br />물어보세요.
                </h2>
                <p className="mb-10" style={{ fontSize: '16px', lineHeight: 1.7, color: CONTENT_INK, opacity: 0.8 }}>
                  관심 있는 지원사업이나 신청 자격이 궁금하다면 문의를 남겨 주세요.
                  담당 상담사가 확인 후 연락드립니다.
                </p>

                <div className="space-y-6">
                  <div>
                    <div className="text-[10px] tracking-[0.35em] uppercase mb-2" style={{ color: CONTENT_MUTED }}>
                      센터
                    </div>
                    <div className="text-[14px]" style={{ color: CONTENT_INK }}>서울특별시 중구 세종대로 110</div>
                    <div className="text-[14px]" style={{ color: CONTENT_INK, opacity: 0.7 }}>전국 17개 시·도 연계</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.35em] uppercase mb-2" style={{ color: CONTENT_MUTED }}>
                      문의
                    </div>
                    <a
                      href="mailto:help@dabom.kr"
                      className="block text-[14px] cursor-pointer hover:opacity-60 transition-opacity"
                      style={{ color: CONTENT_INK }}
                    >
                      help@dabom.kr
                    </a>
                    <a
                      href="https://pf.kakao.com"
                      target="_blank"
                      rel="noreferrer"
                      className="block text-[14px] cursor-pointer hover:opacity-60 transition-opacity"
                      style={{ color: CONTENT_INK }}
                    >
                      카카오톡 채널
                    </a>
                  </div>
                  <div className="pt-4 text-[12px]" style={{ color: CONTENT_MUTED }}>
                    영업일 기준 1~2일 내 답변드립니다.
                  </div>
                </div>
              </div>

              {/* Right — form inside big rounded soft panel */}
              <div className="lg:col-span-7">
                <div
                  className="p-8 md:p-10"
                  style={{
                    borderRadius: '44px',
                    background: `${accent}0a`,
                    border: `1px solid ${accent}1f`,
                  }}
                >
                  <form
                    data-readdy-form
                    id="daoti8dscq3ovkco3ph0"
                    onSubmit={onSubmitInquiry}
                    className="space-y-6"
                  >
                    {/* honeypot */}
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="lumen-honey"
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <FieldInput label="이름" name="name" required />
                      <FieldInput label="이메일" name="email" type="email" required />
                    </div>

                    <div>
                      <FieldLabel>관심 분야</FieldLabel>
                      <div className="flex flex-wrap gap-2">
                        {(['창업', '취업', '교육'] as const).map((k) => {
                          const isActive = interest === k;
                          return (
                            <button
                              key={k}
                              type="button"
                              onClick={() => setInterest(k)}
                              className="cursor-pointer text-[11px] tracking-[0.28em] uppercase whitespace-nowrap px-5 h-[38px] rounded-full transition-all"
                              style={{
                                background: isActive ? accent : '#fff',
                                color: isActive ? '#fff' : CONTENT_INK,
                                border: `1px solid ${isActive ? accent : CONTENT_BORDER}`,
                              }}
                            >
                              {k}
                            </button>
                          );
                        })}
                      </div>
                      <input type="hidden" name="interest" value={interest} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <FieldInput label="상담 희망일" name="preferred_date" type="date" lang="ko" />
                      <FieldInput label="거주 지역" name="location" placeholder="예: 서울 강남구" />
                    </div>

                    <div>
                      <FieldLabel>관심 사업명</FieldLabel>
                      <input
                        type="text"
                        name="budget"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="예: 청년창업 도약패키지"
                        className="w-full outline-none px-5 h-[50px] text-[15px] rounded-full transition-all"
                        style={{ color: CONTENT_INK, background: '#fff', border: `1px solid ${CONTENT_BORDER}` }}
                      />
                    </div>

                    <div>
                      <FieldLabel>문의 내용</FieldLabel>
                      <textarea
                        name="message"
                        rows={3}
                        maxLength={500}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="w-full outline-none px-5 py-4 text-[15px] resize-none rounded-3xl transition-all focus:bg-white"
                        style={{
                          color: CONTENT_INK,
                          background: '#fff',
                          border: `1px solid ${CONTENT_BORDER}`,
                        }}
                      />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-6 pt-2">
                      <button
                        type="submit"
                        disabled={formStatus === 'sending'}
                        className="group inline-flex items-center gap-3 h-[48px] pl-6 pr-5 rounded-full text-[11px] tracking-[0.32em] uppercase whitespace-nowrap cursor-pointer transition-all hover:scale-[1.02] disabled:opacity-60"
                        style={{ background: accent, color: '#fff' }}
                      >
                        {formStatus === 'sending' ? '보내는 중…' : '문의 보내기'}
                        <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.22)', color: '#fff' }}>
                          <i className="ri-arrow-right-line text-[13px]" />
                        </span>
                      </button>

                      {formStatus === 'success' && (
                        <span className="text-[12px]" style={{ color: accent }}>
                          감사합니다. 곧 연락드릴게요.
                        </span>
                      )}
                      {formStatus === 'error' && (
                        <span className="text-[12px]" style={{ color: '#b83a3a' }}>
                          {formError}
                        </span>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </section>

          {/* ─────────── FOOTER — big rounded top ─────────── */}
          <footer
            style={{
              background: accent,
              color: '#fff',
              borderTopLeftRadius: '64px',
              borderTopRightRadius: '64px',
              marginTop: '40px',
            }}
          >
            <div className="max-w-[1180px] mx-auto px-8 md:px-14 pt-24 pb-10">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
                <div className="md:col-span-6">
                  <div className="font-logo-b text-4xl md:text-5xl leading-none" style={{ letterSpacing: '-0.04em' }}>
                    다봄
                  </div>
                  <div className="mt-4 text-[11px] tracking-[0.4em] uppercase" style={{ opacity: 0.8 }}>
                    국비지원 통합 플랫폼
                  </div>
                  <div className="mt-2 text-[13px]" style={{ opacity: 0.85 }}>
                    서울 · 전국
                  </div>
                </div>

                <div className="md:col-span-3">
                  <div className="text-[10px] tracking-[0.35em] uppercase mb-3" style={{ opacity: 0.75 }}>
                    문의
                  </div>
                  <a href="mailto:help@dabom.kr" className="block text-[14px] cursor-pointer hover:opacity-70 transition-opacity">
                    help@dabom.kr
                  </a>
                  <a href="https://pf.kakao.com" target="_blank" rel="noreferrer" className="block text-[14px] cursor-pointer hover:opacity-70 transition-opacity mt-1">
                    카카오톡 채널
                  </a>
                </div>

                <div className="md:col-span-3 md:text-right">
                  <button
                    type="button"
                    onClick={scrollToTop}
                    className="inline-flex items-center gap-3 h-[42px] px-5 rounded-full text-[10px] tracking-[0.32em] uppercase whitespace-nowrap cursor-pointer group transition-all hover:scale-[1.03]"
                    style={{ background: 'rgba(255,255,255,0.14)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
                  >
                    맨 위로
                    <i className="ri-arrow-up-line text-[14px] transition-transform group-hover:-translate-y-0.5" />
                  </button>
                </div>
              </div>

              <div className="pt-6 border-t border-white/20 flex flex-wrap items-center justify-between gap-4 text-[11px] tracking-[0.3em] uppercase" style={{ opacity: 0.75 }}>
                <span>© 2026 다봄</span>
                <span>국비지원 · 공공데이터</span>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] tracking-[0.35em] uppercase mb-3" style={{ color: CONTENT_MUTED }}>
      {children}
    </div>
  );
}

function FieldInput({
  label, name, type = 'text', required, placeholder, lang,
}: {
  label: string; name: string; type?: string; required?: boolean; placeholder?: string; lang?: string;
}) {
  return (
    <div>
      <FieldLabel>{label}{required ? ' *' : ''}</FieldLabel>
      <input
        type={type}
        name={name}
        required={required}
        placeholder={placeholder}
        lang={lang}
        className="w-full outline-none px-5 h-[50px] text-[15px] rounded-full transition-all"
        style={{
          color: CONTENT_INK,
          background: '#fff',
          border: `1px solid ${CONTENT_BORDER}`,
        }}
      />
    </div>
  );
}